import React, { useState } from "react";
import {
    IonButton,
    IonInput,
    IonItem,
    IonLabel,
    IonAlert,
    IonLoading,
    IonIcon,
    IonCheckbox,
} from "@ionic/react";

import useFormValidation from "../../helpers/FormValidationHelper";
import ValidateUgyfelszam from "../../helpers/validators/ValidateUgyfelszam";
import { useAuth } from "../../context/Auth";
import {
    setLoading,
    setInitError,
    hasError,
    addApiErrors,
    ErrorsList,
} from "../../helpers/GlobalHelpers";
import { callOutline, mailOutline } from "ionicons/icons";
import { postAssign } from "../../services/GlobalServices";

const INIT_STATE = {
    ugyfelszam: "",
};

interface Props {
    setCurrentPage: (num: number) => void;
    setRelation: (data: any) => void;
}

const AssignWelcomePage: React.FC<Props> = ({
    setCurrentPage,
    setRelation,
}) => {
    const { assigns, setLogout, checkAxiosError } = useAuth();
    const { handleChange, values } = useFormValidation(INIT_STATE);
    const [status, setStatus] = useState({
        loading: false,
        error_list: INIT_STATE,
    });

    const [showUgyfelszamAlert, setShowUgyfelszamAlert] = useState("");
    const [checkboxStatus, setCheckboxStatus] = useState(false);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLIonInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            handleSendUgyfelszam();
        }
    };

    const handleSendUgyfelszam = async () => {
        if (!checkboxStatus) {
            addApiErrors(
                "A hozzám rendeléshez fogadja el a jelölőnégyzetet.",
                setStatus
            );
            return;
        }

        setLoading(true, setStatus);
        setInitError(INIT_STATE, setStatus);

        const errors = ValidateUgyfelszam(values);
        if (hasError(errors)) {
            setLoading(false, setStatus);
            addApiErrors(errors, setStatus);
        } else {
            postAssign(values.ugyfelszam)
                .then((res: any) => {
                    setLoading(false, setStatus);

                    setRelation(res.data.data);

                    if (res.data.wait_to_verify) {
                        setCurrentPage(3);
                    } else if (res.data.has_phone) {
                        setCurrentPage(2);
                    } else {
                        setCurrentPage(1);
                    }
                })
                .catch((err: any) => {
                    checkAxiosError(err);
                    setLoading(false, setStatus);

                    if (!err.response) {
                        addApiErrors("Hálózati hiba.", setStatus);
                    } else {
                        addApiErrors(err.response.data.message, setStatus);
                    }

                    setShowUgyfelszamAlert(err.response.data.message);
                });
        }
    };

    return (
        <>
            <h4>Üdvözöljük!</h4>
            <p>
                Hogy használni tudja szolgáltatásunkat, kérjük rendelje magához
                szerződéseit.
                <br />
                <br />
                Ehhez adja meg az ügyfél azonosítóját, melyet megtalál a számla
                vagy szerződése fejlécében <b>Partner azonosító</b> néven.
            </p>

            <div className="form">
                <IonItem
                    lines="none"
                    className={
                        "rounded-input " +
                        (Boolean(status.error_list) &&
                        status.error_list.ugyfelszam
                            ? "error"
                            : "")
                    }
                >
                    <IonLabel position="floating">Partner azonosító</IonLabel>
                    <IonInput
                        onKeyDown={handleKeyDown}
                        clearOnEdit={false}
                        type="text"
                        inputmode="text"
                        name="ugyfelszam"
                        onIonChange={handleChange}
                        value={values.ugyfelszam}
                        required
                        autofocus={true}
                    ></IonInput>
                </IonItem>
                <IonItem
                    lines="none"
                    className={"checkbox-input backgroundTransparent"}
                >
                    <IonCheckbox
                        checked={checkboxStatus}
                        onIonChange={(e) => setCheckboxStatus(e.detail.checked)}
                    />
                    <IonLabel class="ion-text-wrap">
                        Javasoljuk, hogy az elsődleges felhasználó a szerződő
                        legyen, mert csak az elsődleges felhasználó írhat alá
                        szerződést, módosíthat adatot és rendelhet meg
                        szolgáltatást az applikáción keresztül, valamint ő
                        engedélyezheti további felhasználó hozzáadását es
                        törölhet másodlagos felhasználókat
                    </IonLabel>
                </IonItem>
            </div>

            <ErrorsList errors={status.error_list} />

            <IonButton
                color="success"
                expand="block"
                disabled={!checkboxStatus}
                onClick={handleSendUgyfelszam}
            >
                Hozzám rendelés
            </IonButton>

            {assigns.length === 0 ? (
                <IonButton color="medium" expand="block" onClick={setLogout}>
                    Kijelentkezés
                </IonButton>
            ) : (
                <IonButton
                    color="medium"
                    expand="block"
                    routerLink="/my/dashboard"
                    routerDirection="none"
                >
                    Vissza a főoldalra
                </IonButton>
            )}

            <br />
            <p>
                Ha problémát észlelt kérjük keresse fel ügyintézőnket az alábbi
                elérhetőségeken.
            </p>
            <IonButton
                color="medium"
                expand="block"
                href="mailto:hello@mypatent.hu"
                target="_blank"
            >
                <IonIcon slot="start" icon={mailOutline} />
                hello@mypatent.hu
            </IonButton>
            <IonButton
                color="medium"
                expand="block"
                href="tel:+36305060111"
                target="_blank"
            >
                <IonIcon slot="start" icon={callOutline} />
                +36 30 50 60 111
            </IonButton>
            <br />
            <IonButton
                color="warning"
                expand="block"
                href="https://securitypatent.hu/szolgaltatasok/patentplusz/mypatent-ugyfel-applikacio/"
                target="_blank"
            >
                Még nem vagyok Patent ügyfél
            </IonButton>

            <IonLoading isOpen={status.loading} message={"Kérem várjon..."} />

            <IonAlert
                isOpen={showUgyfelszamAlert !== ""}
                onDidDismiss={() => setShowUgyfelszamAlert("")}
                header={"Hiba"}
                message={showUgyfelszamAlert}
                buttons={[
                    {
                        text: "Újra próbálom",
                        role: "cancel",
                    },
                ]}
            />
        </>
    );
};

export default AssignWelcomePage;
